import { useEffect, useState } from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { getRateLimitResetTime, formatRateLimitTime, isRateLimited } from '@/lib/auth-cache';

interface RateLimitIndicatorProps {
  onRateLimitChange?: (isLimited: boolean) => void;
}

export function RateLimitIndicator({ onRateLimitChange }: RateLimitIndicatorProps) {
  const [isLimited, setIsLimited] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  useEffect(() => {
    const checkRateLimit = () => {
      const limited = isRateLimited();
      const timeRemaining = getRateLimitResetTime();

      setIsLimited(limited);
      setRemainingTime(timeRemaining);

      if (onRateLimitChange) {
        onRateLimitChange(limited);
      }
    };

    // Check immediately
    checkRateLimit();

    // Check every second while rate-limited
    const interval = setInterval(checkRateLimit, 1000);

    return () => clearInterval(interval);
  }, [onRateLimitChange]);

  if (!isLimited || remainingTime === 0) {
    return null;
  }

  const timeDisplay = formatRateLimitTime(remainingTime);

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
      <div className="flex-shrink-0">
        <AlertTriangle size={16} className="text-amber-600" />
      </div>
      <div className="flex items-center gap-1">
        <Clock size={14} className="text-amber-600" />
        <span>
          Rate limit active. Try again in <strong>{timeDisplay}</strong>
        </span>
      </div>
    </div>
  );
}
